import { logger } from 'features/logger/logger';
import type {
  TraceItem,
  TraceEvent,
  TraceField,
  TraceMetadata,
  TraceSpanStart,
  TraceSpanEnd,
} from '@castoredc/castor_prose';

type LoggerMethod = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_TO_LOGGER = {
  TRACE: 'debug',
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
} as const satisfies Record<string, LoggerMethod>;

type TraceLevelKey = keyof typeof LEVEL_TO_LOGGER;

const RESERVED_DETAIL_KEYS = new Set([
  'message',
  'metadata',
  'span',
  'timestamp',
]);
const STRING_FIELD_MAX_CHARS = 256;

type FlatFieldValue = string | number | boolean;

function isTraceLevelKey(level: string): level is TraceLevelKey {
  return Object.hasOwn(LEVEL_TO_LOGGER, level);
}

function resolveLoggerMethod(level: string | undefined): LoggerMethod {
  if (level && isTraceLevelKey(level)) {
    return LEVEL_TO_LOGGER[level];
  }
  return 'info';
}

function truncateString(value: string): string {
  if (value.length <= STRING_FIELD_MAX_CHARS) return value;
  return value.slice(0, STRING_FIELD_MAX_CHARS - 1) + '…';
}

function convertFieldValue(field: TraceField): FlatFieldValue | undefined {
  const v = field.value;
  if (!v) return undefined;
  switch (v.$case) {
    case 'str':
      return truncateString(v.value);
    case 'i64':
    case 'u64':
    case 'float64':
      return v.value;
    case 'boolean':
      return v.value;
    default:
      return undefined;
  }
}

function flattenFields(fields: TraceField[]): Record<string, FlatFieldValue> {
  const out: Record<string, FlatFieldValue> = {};
  for (const field of fields) {
    const converted = convertFieldValue(field);
    if (converted === undefined) continue;
    const key = RESERVED_DETAIL_KEYS.has(field.key)
      ? `field_${field.key}`
      : field.key;
    out[key] = converted;
  }
  return out;
}

type SpanIndex = Map<number, { name: string; parentId?: number }>;

const EMPTY_SPAN_INDEX: SpanIndex = new Map();

function indexSpans(items: TraceItem[]): SpanIndex {
  let index: SpanIndex | null = null;
  for (const item of items) {
    if (item.item?.$case === 'spanStart' || item.item?.$case === 'spanEnd') {
      const span = item.item.value;
      const entry: { name: string; parentId?: number } = { name: span.name };
      if (span.parentId !== undefined) {
        entry.parentId = span.parentId;
      }
      (index ??= new Map()).set(span.id, entry);
    }
  }
  return index ?? EMPTY_SPAN_INDEX;
}

type FlatMetadata = { target: string; file?: string; line?: number };

function flattenMetadata(metadata: TraceMetadata): FlatMetadata {
  const out: FlatMetadata = { target: metadata.target };
  if (metadata.file !== undefined) out.file = metadata.file;
  if (metadata.line !== undefined) out.line = metadata.line;
  return out;
}

function buildEventDetails(
  event: TraceEvent,
  spans: SpanIndex
): Record<string, unknown> {
  const details: Record<string, unknown> = {
    fields: flattenFields(event.fields),
    timestamp: event.timestamp,
  };
  if (event.metadata) {
    details['metadata'] = flattenMetadata(event.metadata);
  }
  if (event.spanId !== undefined) {
    const enclosing = spans.get(event.spanId);
    details['span'] = enclosing
      ? {
          spanId: event.spanId,
          name: enclosing.name,
          ...(enclosing.parentId !== undefined && {
            parentId: enclosing.parentId,
          }),
        }
      : { spanId: event.spanId };
  }
  return details;
}

function forwardEvent(event: TraceEvent, spans: SpanIndex): void {
  const method = resolveLoggerMethod(event.metadata?.level);
  logger[method](`[prose] ${event.message}`, {
    details: buildEventDetails(event, spans),
  });
}

function buildSpanDetails(span: TraceSpanEnd): Record<string, unknown> {
  const details: Record<string, unknown> = {
    span: {
      name: span.name,
      spanId: span.id,
      ...(span.parentId !== undefined && { parentId: span.parentId }),
      endTimestamp: span.endTimestamp,
      ...(span.duration !== undefined && { durationMs: span.duration }),
    },
    fields: flattenFields(span.fields),
  };
  if (span.metadata) {
    details['metadata'] = flattenMetadata(span.metadata);
  }
  return details;
}

function forwardSpan(span: TraceSpanEnd): void {
  const method = resolveLoggerMethod(span.metadata?.level);
  logger[method](`[prose] span end: ${span.name}`, {
    details: buildSpanDetails(span),
  });
}

function buildSpanStartDetails(span: TraceSpanStart): Record<string, unknown> {
  const details: Record<string, unknown> = {
    span: {
      name: span.name,
      spanId: span.id,
      ...(span.parentId !== undefined && { parentId: span.parentId }),
      startTimestamp: span.startTimestamp,
    },
  };
  if (span.metadata) {
    details['metadata'] = flattenMetadata(span.metadata);
  }
  return details;
}

function forwardSpanStart(span: TraceSpanStart): void {
  const method = resolveLoggerMethod(span.metadata?.level);
  logger[method](`[prose] span start: ${span.name}`, {
    details: buildSpanStartDetails(span),
  });
}

let hasWarnedOnFailure = false;

export function __resetForTests(): void {
  hasWarnedOnFailure = false;
}

export function handleProseTraces(items: TraceItem[]): void {
  try {
    const spans = indexSpans(items);
    for (const item of items) {
      if (item.item?.$case === 'event') {
        forwardEvent(item.item.value, spans);
      } else if (item.item?.$case === 'spanStart') {
        forwardSpanStart(item.item.value);
      } else if (item.item?.$case === 'spanEnd') {
        forwardSpan(item.item.value);
      }
    }
  } catch (err) {
    if (!hasWarnedOnFailure) {
      hasWarnedOnFailure = true;
      console.warn('[prose-trace] forwarding failed', err);
    }
  }
}
